import React, {
  useCallback,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import Cookies from "js-cookie";
import CookieSettingsModal from "./CookieSettingsModal";

const STORAGE_KEY = "cookie_consent_v1";
// 180 gün geçerlilik süresi
const COOKIE_EXPIRES = 180;

const defaultConsent = {
  necessary: true,
  thirdPartyMedia: null,
  ts: null,
};

const CookieConsentContext = createContext(null);

function loadConsent() {
  try {
    const raw = Cookies.get(STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);
    return data;
  } catch {
    return null;
  }
}

function persistConsent(payload) {
  try {
    Cookies.set(STORAGE_KEY, JSON.stringify(payload), {
      expires: COOKIE_EXPIRES,
      secure: window.location.protocol === "https:", // Sadece HTTPS'de güvenli gönderim
      sameSite: "lax", // CSRF koruması için standart değer
    });
  } catch {}
}

function getInitialConsent() {
  const loaded = loadConsent();
  return loaded ? { ...defaultConsent, ...loaded } : defaultConsent;
}

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(getInitialConsent);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const openSettings = () => setSettingsOpen(true);
  const closeSettings = () => setSettingsOpen(false);
  const toggleSettings = () => setSettingsOpen((v) => !v);

  const saveConsent = useCallback(
    (next) => {
      const payload = { ...consent, ...next, ts: Date.now() };
      setConsent(payload);
      persistConsent(payload);
    },
    [consent],
  );

  const value = useMemo(
    () => ({
      consent,
      openSettings,
      closeSettings,
      toggleSettings,

      acceptAll: () => {
        saveConsent({ thirdPartyMedia: true });
        closeSettings();
      },
      rejectAll: () => {
        saveConsent({ thirdPartyMedia: false });
        closeSettings();
      },
      setThirdPartyMedia: (v) => saveConsent({ thirdPartyMedia: !!v }),

      resetConsent: () => {
        setConsent(defaultConsent);
        try {
          Cookies.remove(STORAGE_KEY);
        } catch {}
      },
    }),
    [consent, saveConsent],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      <CookieSettingsModal isOpen={settingsOpen} toggle={toggleSettings} />
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx)
    throw new Error(
      "useCookieConsent must be used within CookieConsentProvider",
    );
  return ctx;
}
